import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { init as initTextMetrics, utils as textMetricsUtils } from 'text-metrics';
import { Card } from '@/components/ui/swimlane/layout/body/cards/Card';
import {
  getDateLabel,
  getHasDescription,
  getHasLabels,
  getHasParentTitle,
  getHasProgress,
  getParentTitle,
} from '@/components/ui/swimlane/layout/body/cards/CardUtils';
import {
  MAX_CARD_SUBITEMS_HEIGHT,
  SUBITEM_GAP,
  SUBITEM_HEIGHT,
} from '@/components/ui/swimlane/layout/constants';
import type { ICard } from '../../types';
import { buildCardId } from '../../utils';
import { useSwimlaneRootProps } from '../SwimlaneRootPropsContext';

// `text-metrics` re-exports utils, but `@types/text-metrics` does not declare them
// so we need to declare some of them here to use in the code
declare module 'text-metrics' {
  interface TextMetrics {
    style: CSSStyleDeclaration;
  }

  const utils: {
    prop: <T, K extends keyof T>(src: T | null | undefined, attr: K, defaultValue?: T[K]) => T[K];
    getFont: (style: CSSStyleDeclaration, options: CSSStyleDeclaration) => string;
    getContext2d: (font: string) => CanvasRenderingContext2D;
    prepareText: (text: string) => string;
    normalizeWhitespace: (text: string, whiteSpace?: string) => string;
    getStyledText: (text: string, style: CSSStyleDeclaration) => string;
    computeLinesBreakAll: ({
      ctx,
      text,
      max,
      wordSpacing,
      letterSpacing,
    }: {
      ctx: CanvasRenderingContext2D;
      text: string;
      max: number;
      wordSpacing: string;
      letterSpacing: string;
    }) => string[];
    computeLinesDefault: ({
      ctx,
      text,
      max,
      wordSpacing,
      letterSpacing,
    }: {
      ctx: CanvasRenderingContext2D;
      text: string;
      max: number;
      wordSpacing: string;
      letterSpacing: string;
    }) => string[];
  };
}

const skeletonCardProps: ICard = {
  itemID: '',
  rowID: '',
  columnID: '',
  title: '_',
  description: '',
  startDate: '',
  endDate: '',
  borderColor: '',
  labels: [],
  progress: null,
  linkedItems: 0,
  keyDates: 0,
  comments: 0,
  attachments: 0,
  subItems: [],
  isDeletable: false,
} as const;

export const minCardHeight = 46;

const cardRenderPropsArray: [string, Partial<ICard>][] = [
  ['base', {}],
  ['parentTitle', { parentId: '_', parentTitle: '_' }],
  ['date', { bucketLabel: '_', startDate: '_', endDate: '_' }],
  ['description', { description: '_' }],
  ['labels', { labels: [{ value: '_', fieldName: '_' }] }],
  ['progress', { progress: 0 }],
  ['subItems', { subItems: [{ id: '_', title: '_' }] }],
] as const;

const generateCardPropsCombinations = () => {
  const resultMap = new Map();

  // Helper function to generate combinations
  function getCombination(start: number, currentIds: string[], currentRecord: Partial<ICard>) {
    if (currentIds.length > 0 && currentIds.includes('base')) {
      const itemID = currentIds.join('-');
      resultMap.set(itemID, { ...skeletonCardProps, ...currentRecord, itemID }); // Add the current combination to the map
    }

    for (let i = start; i < cardRenderPropsArray.length; i++) {
      const entry = cardRenderPropsArray[i];
      if (!entry) continue;
      const [id, record] = entry;
      currentIds.push(id); // Add the current ID to the combination
      getCombination(i + 1, currentIds, { ...currentRecord, ...record }); // Merge the records and recurse
      currentIds.pop(); // Backtrack to remove the ID
    }
  }

  getCombination(0, [], {}); // Start the recursion
  return resultMap;
};

export const cardPropsCombinations = generateCardPropsCombinations();

const parseStyleValue = (value?: string) => {
  const parsedValue = parseFloat(value || '');
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const getDynamicElementStyles = (element: HTMLElement | null) => {
  if (!element) return null;
  const computedStyle = getComputedStyle(element);
  const paddingTop = parseStyleValue(computedStyle.paddingTop);
  const paddingRight = parseStyleValue(computedStyle.paddingRight);
  const paddingBottom = parseStyleValue(computedStyle.paddingBottom);
  const paddingLeft = parseStyleValue(computedStyle.paddingLeft);
  const width = element.offsetWidth - paddingLeft - paddingRight;
  const lineHeight = parseStyleValue(computedStyle.lineHeight);

  // `text-metrics` creates canvas element and styles each time `lines` called, so we need to memoize them
  const styleObject: Record<string, string> = {};
  for (let i = 0; i < computedStyle.length; i++) {
    const key = computedStyle[i];
    if (key) {
      styleObject[key] = computedStyle.getPropertyValue(key);
    }
  }
  styleObject.width = String(width);
  const textMetrics = initTextMetrics(styleObject);
  const { styles } = textMetrics.parseArgs('');
  const whiteSpace: string =
    (textMetricsUtils.prop(styles, 'white-space') as string) ||
    textMetrics.style.getPropertyValue('white-space');
  const font = textMetricsUtils.getFont(
    textMetrics.style,
    styles as unknown as CSSStyleDeclaration,
  );
  const wordBreak: string =
    (textMetricsUtils.prop(styles, 'word-break') as string) ||
    textMetrics.style.getPropertyValue('word-break');
  const letterSpacing: string =
    (textMetricsUtils.prop(styles, 'letter-spacing') as string) ||
    textMetrics.style.getPropertyValue('letter-spacing');
  const wordSpacing: string =
    (textMetricsUtils.prop(styles, 'word-spacing') as string) ||
    textMetrics.style.getPropertyValue('word-spacing');
  // COULDDO: Use OffscreenCanvas to calculate text metrics off the main thread
  const ctx = textMetricsUtils.getContext2d(font);

  return {
    width,
    lineHeight,
    paddingTop,
    paddingBottom,
    // rewrite `text-metrics` implementation using memoized values
    lines(str: string) {
      const text = textMetricsUtils.prepareText(
        textMetricsUtils.normalizeWhitespace(str, whiteSpace),
      );
      const styledText = textMetricsUtils.getStyledText(text, textMetrics.style);

      // Different scenario when break-word is allowed
      if (wordBreak === 'break-all') {
        return textMetricsUtils.computeLinesBreakAll({
          ctx,
          text: styledText,
          max: width,
          wordSpacing,
          letterSpacing,
        }).length;
      }

      return textMetricsUtils.computeLinesDefault({
        ctx,
        text: styledText,
        max: width,
        wordSpacing,
        letterSpacing,
      }).length;
    },
  };
};

type TDynamicElementStyles = ReturnType<typeof getDynamicElementStyles>;

const maxTextLength = 312; // > 5 lines of text, used to calculate the max available height for the text and reduce the number of calculations
const measureTextHeight = (
  text: string,
  dynamicElementStyles: TDynamicElementStyles,
  maxLines = Number.MAX_SAFE_INTEGER,
  canBeEmpty = true,
): number => {
  if (!dynamicElementStyles || (canBeEmpty && !text)) return 0;

  const { paddingTop, paddingBottom, lineHeight, lines } = dynamicElementStyles;

  if (!lineHeight) return 0;

  if (maxLines === 1 || !text) return lineHeight + paddingTop + paddingBottom;

  return (
    Math.min(lines(text.slice(0, maxTextLength)), maxLines) * lineHeight +
    paddingTop +
    paddingBottom
  );
};

type TCardDynamicStyles = Record<'title' | 'description', TDynamicElementStyles | null>;

export type TCardHeightCalculationState = {
  defaultCardHeight: number;
  calculateCardHeight: (cardProps: ICard) => number;
};

type TCardHeightCalculationStateManagerProps = {
  isCompact: boolean;
  isItemDateVisible: boolean;
  isItemDescriptionVisible: boolean;
  isItemDetailsVisible: boolean;
  maxCardTitleLines: number;
  readOnly: boolean;
  areLabelsEnabledInCompactSL: boolean;
  showExpandedSubItems: boolean;
};

export const hasCardSizePropsChanged = (prevProps?: ICard, nextProps?: ICard) => {
  if (prevProps === nextProps) return false;
  if (!prevProps || !nextProps || typeof prevProps !== typeof nextProps) return true;

  const { title: prevTitle, labels: prevLabels, description: prevDescription } = prevProps;
  const { title, labels, description } = nextProps;

  return (
    !getParentTitle(prevProps) !== !getParentTitle(nextProps) ||
    prevTitle !== title ||
    prevDescription !== description ||
    getHasProgress(prevProps) !== getHasProgress(nextProps) ||
    !getDateLabel(prevProps) !== !getDateLabel(nextProps) ||
    prevLabels?.length !== labels?.length
  );
};

const calculateCardHeightFactory = ({
  calculatorProps,
  dynamicStyles,
  height,
  minTitleHeight,
  minDescriptionHeight,
}: {
  calculatorProps: TCardHeightCalculationStateManagerProps;
  dynamicStyles: TCardDynamicStyles;
  height: Map<string, number>;
  minTitleHeight: number;
  minDescriptionHeight: number;
}) => {
  const { maxCardTitleLines, showExpandedSubItems } = calculatorProps;
  const additionalSubItemHeight = SUBITEM_HEIGHT.value + SUBITEM_GAP.value;
  const maxAdditionalSubItemsHeight = MAX_CARD_SUBITEMS_HEIGHT.value - SUBITEM_HEIGHT.value;

  return (cardProps: ICard): number => {
    const { title, description, subItems } = cardProps;
    const parts = ['base'];

    if (getHasParentTitle(cardProps)) parts.push('parentTitle');

    const titleHeightDiff =
      maxCardTitleLines === 1
        ? 0
        : measureTextHeight(title, dynamicStyles.title, maxCardTitleLines, false) - minTitleHeight;

    if (getDateLabel(cardProps)) parts.push('date');

    let descriptionHeightDiff = 0;
    if (getHasDescription(cardProps, calculatorProps)) {
      parts.push('description');
      descriptionHeightDiff =
        measureTextHeight(description, dynamicStyles.description, 3, true) - minDescriptionHeight;
    }

    if (getHasLabels(cardProps, calculatorProps)) parts.push('labels');

    if (getHasProgress(cardProps)) parts.push('progress');

    let subItemsHeightDiff = 0;
    if (subItems?.length) {
      parts.push('subItems');
      if (showExpandedSubItems && subItems.length > 1) {
        // Calculate height of additional sub-items beyond the first one (base-subItems 1 sub-item)
        const additionalSubItemsCount = subItems.length - 1;
        const additionalSubItemsHeight = additionalSubItemsCount * additionalSubItemHeight;

        subItemsHeightDiff = Math.min(maxAdditionalSubItemsHeight, additionalSubItemsHeight);
      }
    }

    const cardHeight = height.get(parts.join('-')) ?? height.get('base') ?? minCardHeight;

    return cardHeight + titleHeightDiff + descriptionHeightDiff + subItemsHeightDiff;
  };
};

export const CardHeightCalculationStateManager = ({
  onStateChange,
}: {
  onStateChange: (state: TCardHeightCalculationState) => void;
}) => {
  const {
    isCompact,
    isItemDateVisible,
    isItemDescriptionVisible,
    isItemDetailsVisible,
    maxCardTitleLines,
    readOnly,
    areLabelsEnabledInCompactSL,
    showExpandedSubItems,
  } = useSwimlaneRootProps();
  const calculatorProps = useMemo(
    () => ({
      isCompact,
      isItemDateVisible,
      isItemDescriptionVisible,
      isItemDetailsVisible,
      maxCardTitleLines,
      readOnly,
      areLabelsEnabledInCompactSL,
      showExpandedSubItems,
    }),
    [
      isCompact,
      isItemDateVisible,
      isItemDescriptionVisible,
      isItemDetailsVisible,
      maxCardTitleLines,
      readOnly,
      areLabelsEnabledInCompactSL,
      showExpandedSubItems,
    ],
  );
  const [calculatorState, setCalculatorState] = useState<TCardHeightCalculationState | null>(null);
  const mountedCardsRef = useRef(0);
  const boxRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependency array is intentionally limited to prevent excessive re-renders
  useEffect(() => {
    mountedCardsRef.current = 0;
    setCalculatorState(null);
  }, [calculatorProps, onStateChange]);

  const onCardMount = useCallback(() => {
    mountedCardsRef.current++;

    if (mountedCardsRef.current === cardPropsCombinations.size) {
      const height = new Map<string, number>();
      let minTitleHeight = 0;
      let minDescriptionHeight = 0;
      let dynamicStyles: TCardDynamicStyles = { title: null, description: null };

      for (const [key, props] of cardPropsCombinations) {
        const cardId = buildCardId(props);
        const cardElement = boxRef.current?.querySelector(
          `[data-card-id="${cardId}"]`,
        ) as HTMLElement;
        if (cardElement) {
          height.set(key, cardElement.offsetHeight);

          if (props.itemID === 'base-description') {
            const title = cardElement.querySelector('[data-testid="card-title"]');
            const description = cardElement.querySelector('[data-testid="card-description"]');
            dynamicStyles = {
              title: getDynamicElementStyles(title as HTMLElement),
              description: getDynamicElementStyles(description as HTMLElement),
            };
            minTitleHeight = measureTextHeight('_', dynamicStyles.title, 1, false);
            minDescriptionHeight = measureTextHeight('_', dynamicStyles.description, 1, false);
          }
        }
      }

      const calculateCardHeight = calculateCardHeightFactory({
        height,
        dynamicStyles,
        minTitleHeight,
        minDescriptionHeight,
        calculatorProps,
      });
      const nextState = {
        defaultCardHeight: calculateCardHeight(skeletonCardProps),
        calculateCardHeight,
      };

      onStateChange(nextState);
      setCalculatorState(nextState);
    }
  }, [calculatorProps, onStateChange]);

  const keyPrefix = Object.values(calculatorProps).join('-');

  return (
    !calculatorState && (
      <Box
        ref={boxRef}
        sx={{
          position: 'fixed',
          top: '100%',
          left: '100%',
          marginTop: 1000,
          visibility: 'hidden',
        }}
      >
        {[...cardPropsCombinations].map(([key, props]) => (
          <Card
            key={`${keyPrefix}-${key}`}
            {...skeletonCardProps}
            {...props}
            itemID={key}
            onCardMount={onCardMount}
          />
        ))}
      </Box>
    )
  );
};
